import { useState } from "react";
import { Link } from "react-router-dom";
import type { Product } from "@/lib/api";
import { useWishlist } from "@/hooks/useWishlist";
import { useCart } from "@/hooks/useCart";

interface WishlistCardProps {
  product: Product;
}

export default function WishlistCard({ product }: WishlistCardProps) {
  const { removeFromWishlist } = useWishlist();
  const { addItem } = useCart();
  const [addedToCart, setAddedToCart] = useState(false);
  const [hovered, setHovered] = useState(false);

  const hasDiscount =
    product.originalPrice && product.originalPrice > product.price;

  const handleAddToCart = () => {
    addItem({
      productId: product.id,
      name: product.name,
      price: product.price,
      size: product.sizes[0],
      color: product.colors[0].name,
      colorHex: product.colors[0].hex,
      quantity: 1,
      image: product.images[0],
    });
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  return (
    <div
      className="group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image Container */}
      <div
        className="relative w-full overflow-hidden bg-pearl/10"
        style={{ aspectRatio: "3/4" }}
      >
        <Link to={`/products/${product.slug}`} className="block w-full h-full">
          <img
            src={product.images[0]}
            alt={product.name}
            className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-700 ${
              hovered && product.images[1]
                ? "opacity-0 scale-105"
                : "opacity-100 scale-100"
            }`}
          />
          {product.images[1] && (
            <img
              src={product.images[1]}
              alt={`${product.name} detail`}
              className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-700 ${
                hovered ? "opacity-100 scale-105" : "opacity-0 scale-100"
              }`}
            />
          )}
        </Link>

        {/* Remove from Wishlist Button */}
        <button
          onClick={() => removeFromWishlist(product.id)}
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center bg-moonlight/90 backdrop-blur-sm hover:bg-moonlight transition-colors duration-150 cursor-pointer"
          title="Remove from wishlist"
        >
          <i className="ri-heart-fill text-sm text-rose-400" />
        </button>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10 pointer-events-none">
          {product.isNew && (
            <span className="text-[10px] tracking-widest uppercase bg-midnight text-moonlight px-2 py-0.5 font-medium">
              New
            </span>
          )}
          {hasDiscount && (
            <span className="text-[10px] tracking-widest uppercase bg-moon-gold-dark text-midnight px-2 py-0.5 font-medium">
              Sale
            </span>
          )}
        </div>

        {/* Add to Cart Overlay */}
        <div
          className={`absolute bottom-0 left-0 right-0 z-20 transition-transform duration-300 ease-out ${
            hovered ? "translate-y-0" : "translate-y-full"
          }`}
          style={{
            background: "rgba(249,246,240,0.97)",
            backdropFilter: "blur(8px)",
          }}
        >
          <button
            onClick={handleAddToCart}
            className={`w-full text-[10px] tracking-widest uppercase py-3 transition-colors duration-150 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 ${
              addedToCart
                ? "bg-moon-gold-dark/20 text-midnight"
                : "text-midnight/70 hover:text-midnight hover:bg-pearl/40"
            }`}
          >
            {addedToCart ? (
              <>
                <i className="ri-check-line text-xs" /> Added to Cart
              </>
            ) : (
              <>
                <i className="ri-shopping-bag-line text-xs" /> Add to Cart
              </>
            )}
          </button>
        </div>
      </div>

      {/* Product Info */}
      <Link to={`/products/${product.slug}`} className="block cursor-pointer">
        <div className="mt-3 px-0.5">
          <p className="text-xs tracking-wide text-midnight/80 group-hover:text-midnight transition-colors line-clamp-1">
            {product.name}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm font-medium text-midnight">
              ${product.price}
            </span>
            {hasDiscount && (
              <span className="text-xs text-midnight/40 line-through">
                ${product.originalPrice}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 mt-1">
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <i
                  key={i}
                  className={`text-[9px] ${
                    i < Math.floor(product.rating)
                      ? "ri-star-fill text-moon-gold-dark"
                      : "ri-star-line text-pearl"
                  }`}
                />
              ))}
            </div>
            <span className="text-[10px] text-midnight/40">
              ({product.reviewCount})
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
}
